import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api, type ConversationDetail, type ConversationView, type TeamTextView, type TextSummaryResult } from '../../api/client';
import { dial, useCalls } from '../../lib/calls';
import { Icon } from '../../lib/icons';
import { useMessagesLive } from '../../lib/messages';
import { handToWords } from '../../lib/textRouting';
import { Button, Chip, Dialog, Notice } from '../ui';

/**
 * Shared texting in Messages (1.12.32, Teampace 2960): texts to a ring group's or queue's number
 * show which team they're for, who has each one (or how long it's waited), and the team's actions
 * (Take it, Take over, Hand to…, Put back, Resolve); the summary beside them (Stitch AI); and a Call
 * button in every text conversation (2973).
 */

const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** "3m", "1h 5m", "2d": how long since. */
export function sinceWords(since: string | null, now: number): string {
  if (!since) return '';
  const s = Math.max(0, Math.floor((now - Date.parse(since)) / 1000));
  if (s < 60) return 'now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

/** "Billing queue", "Front Office ring group". */
export const teamLabel = (t: Pick<TeamTextView, 'name' | 'kind'>): string => `${t.name} ${t.kind === 'callQueue' ? 'queue' : 'ring group'}`;

export type TeamFilter = 'mine' | 'waiting' | 'all';
const FILTER_KEY = 'stitch.teamTexts.filter';

/** Mine, Waiting or All (remembered on this device). */
export function useTeamFilter(): [TeamFilter, (f: TeamFilter) => void] {
  const [f, setF] = useState<TeamFilter>(() => {
    try {
      const v = localStorage.getItem(FILTER_KEY);
      return v === 'mine' || v === 'waiting' ? v : 'all';
    } catch {
      return 'all';
    }
  });
  return [
    f,
    (next) => {
      setF(next);
      try {
        localStorage.setItem(FILTER_KEY, next);
      } catch {
        // Not kept: fine.
      }
    }
  ];
}

/** Whether a shared conversation shows under a filter. Resolved ones show under All only. */
export function inFilter(t: TeamTextView, f: TeamFilter): boolean {
  if (f === 'mine') return t.mine;
  if (f === 'waiting') return t.state === 'waiting' || (t.state === 'open' && Boolean(t.waitingSince) && t.mine);
  return true;
}

/** A team's conversations in the list: waiting first (urgent, then longest), then open, then resolved. */
export function teamOrder(a: ConversationView, b: ConversationView): number {
  const rank = (c: ConversationView) => (c.team?.state === 'waiting' ? 0 : c.team?.state === 'open' ? 1 : 2);
  const ra = rank(a);
  const rb = rank(b);
  if (ra !== rb) return ra - rb;
  if (ra === 0) return Number(Boolean(b.team?.urgent)) - Number(Boolean(a.team?.urgent)) || Date.parse(a.team?.waitingSince ?? a.lastMessageAt ?? '') - Date.parse(b.team?.waitingSince ?? b.lastMessageAt ?? '');
  return Date.parse(b.lastMessageAt ?? '') - Date.parse(a.lastMessageAt ?? '');
}

/** Ticks every half minute (for "Waiting 3m"). */
export function useMinute(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  return now;
}

/** In a list row: who has it ("You", "Dana"), or how long it's waited, or that it's resolved. */
export function TeamMark({ t }: { t: TeamTextView }) {
  const now = useMinute();
  if (t.state === 'resolved') return <span className="team-mark done">Resolved</span>;
  if (t.state === 'waiting') {
    return (
      <span className={`team-mark wait${t.urgent ? ' urgent' : ''}`} title={t.urgent ? 'Urgent: waiting for someone' : 'Waiting for someone'}>
        {t.urgent ? 'Urgent' : 'Waiting'} {sinceWords(t.waitingSince, now)}
      </span>
    );
  }
  const first = t.owner?.name.split(/\s+/)[0] ?? '';
  return (
    <span className={`team-mark own${t.mine ? ' mine' : ''}`} title={t.mine ? 'You have this' : `${t.owner?.name ?? 'Someone'} has this`}>
      {t.mine ? 'You' : first}
    </span>
  );
}

/** Mine | Waiting | All, above the teams' texts. */
export function TeamFilterBar({ value, onChange, counts }: { value: TeamFilter; onChange: (f: TeamFilter) => void; counts: Record<TeamFilter, number> }) {
  const label = { mine: 'Mine', waiting: 'Waiting', all: 'All' } as const;
  return (
    <div className="team-filter" role="group" aria-label="Show texts">
      {(['mine', 'waiting', 'all'] as const).map((f) => (
        <button key={f} type="button" aria-pressed={value === f} className={value === f ? 'on' : ''} onClick={() => onChange(f)}>
          {label[f]}
          {f !== 'all' && counts[f] > 0 && <span className="team-filter-n">{counts[f]}</span>}
        </button>
      ))}
    </div>
  );
}

/**
 * A team's texts in the list. Signed out of a queue: one line ("Billing queue · signed out ·
 * Sign in"), and its texts only on request, read-only.
 */
export function TeamGroup({
  team,
  list,
  filter,
  domain,
  link,
  onNewText
}: {
  team: TeamTextView;
  list: ConversationView[];
  filter: TeamFilter;
  domain: string;
  link: (c: ConversationView) => ReactNode;
  /** Text someone new from this team's number (3147). */
  onNewText?: () => void;
}) {
  const [peek, setPeek] = useState(false);
  const shown = list.filter((c) => c.team && inFilter(c.team, filter)).sort(teamOrder);
  const waiting = list.filter((c) => c.team?.state === 'waiting').length;
  const out = team.kind === 'callQueue' && !team.signedIn && !team.supervisor;
  return (
    <div className="msg-side-section team-group">
      <div className="msg-side-head">
        <h2 title={teamLabel(team)}>
          <Icon name={team.kind === 'callQueue' ? 'list' : 'users'} size={13} strokeWidth={2.4} />
          {team.name}
          {!team.open && <span className="team-closed">Closed now</span>}
        </h2>
        {!out && waiting > 0 && (
          <span className="team-waiting" aria-label={`${waiting} waiting`}>
            {waiting} waiting
          </span>
        )}
        {onNewText && (
          <button type="button" className="msg-side-btn" aria-label={`Text someone from ${team.name}`} title={`Text someone from ${team.name}`} onClick={onNewText}>
            <Icon name="plus" size={16} strokeWidth={2.4} />
          </button>
        )}
      </div>
      {out ? (
        <div className="team-out">
          <button type="button" className="msg-side-more" aria-expanded={peek} onClick={() => setPeek((v) => !v)}>
            <Icon name={peek ? 'chev' : 'chevRight'} size={13} strokeWidth={2.4} />
            Signed out
          </button>
          <Link className="team-signin" to={`/t/${encodeURIComponent(domain)}/me`}>
            Sign in
          </Link>
          {peek && <div className="msg-side-list">{list.sort(teamOrder).map((c) => link(c))}</div>}
        </div>
      ) : (
        <div className="msg-side-list">
          {shown.map((c) => link(c))}
          {!shown.length && <p className="msg-side-empty">{filter === 'mine' ? 'None of these are yours.' : filter === 'waiting' ? 'Nobody’s waiting.' : 'No texts yet.'}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * Under the header of a shared conversation: the team, who has it, and what you can do. Signed out
 * of the queue: read-only, with Sign in.
 */
export function TeamBar({ domain, c, onDetail }: { domain: string; c: ConversationView; onDetail: (d: ConversationDetail) => void }) {
  const t = c.team!;
  const now = useMinute();
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [handing, setHanding] = useState(false);
  const act = async (what: string, fn: () => Promise<ConversationDetail>) => {
    setBusy(what);
    setError('');
    try {
      onDetail(await fn());
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy('');
    }
  };
  const who =
    t.state === 'resolved' ? (
      <span>Resolved</span>
    ) : t.owner ? (
      t.mine ? (
        <strong>You have this</strong>
      ) : (
        <span>
          <strong>{t.owner.name}</strong> has this
        </span>
      )
    ) : (
      <span className={t.urgent ? 'team-urgent' : undefined}>
        {t.urgent ? 'Urgent · ' : ''}Waiting for someone{t.waitingSince ? ` · ${sinceWords(t.waitingSince, now)}` : ''}
      </span>
    );
  return (
    <div className={`team-bar${t.mine ? ' mine' : ''}`} role="region" aria-label={`${teamLabel(t)} texts`}>
      <Chip tone={t.kind === 'callQueue' ? 'brand' : 'neutral'} small>
        {teamLabel(t)}
      </Chip>
      {!t.open && (
        <Chip tone="warn" small>
          Closed now
        </Chip>
      )}
      <span className="team-bar-who">{who}</span>
      {t.mine && t.waitingSince && t.state === 'open' && <span className="muted">They’re waiting {sinceWords(t.waitingSince, now)}</span>}
      <span className="team-bar-actions">
        {t.kind === 'callQueue' && !t.signedIn && !t.supervisor ? (
          <Link className="btn small secondary" to={`/t/${encodeURIComponent(domain)}/me`}>
            Sign in to {t.name}
          </Link>
        ) : (
          <>
            {t.canTake && (t.state !== 'open' || !t.owner) && (
              <Button size="small" variant="primary" disabled={busy !== ''} onClick={() => act('take', () => api.takeText(domain, c.id))}>
                Take it
              </Button>
            )}
            {t.canTake && t.state === 'open' && t.owner && !t.mine && (
              <Button size="small" disabled={busy !== ''} onClick={() => act('take', () => api.takeText(domain, c.id))}>
                Take over
              </Button>
            )}
            {t.canHand && (
              <Button size="small" variant="ghost" disabled={busy !== ''} onClick={() => setHanding(true)}>
                Hand to…
              </Button>
            )}
            {t.canPutBack && (
              <Button size="small" variant="ghost" disabled={busy !== ''} title="Back in line, for someone else" onClick={() => act('back', () => api.putBackText(domain, c.id))}>
                Put back
              </Button>
            )}
            {t.canResolve && (
              <Button size="small" variant="ghost" icon="check" disabled={busy !== ''} title="Done: a new text from them starts it again" onClick={() => act('resolve', () => api.resolveText(domain, c.id))}>
                Resolve
              </Button>
            )}
          </>
        )}
      </span>
      {error && (
        <div className="team-bar-error">
          <Notice tone="crit">{error}</Notice>
        </div>
      )}
      {handing && (
        <HandDialog
          domain={domain}
          c={c}
          onClose={() => setHanding(false)}
          onDone={(d) => {
            setHanding(false);
            onDetail(d);
          }}
        />
      )}
    </div>
  );
}

function HandDialog({ domain, c, onClose, onDone }: { domain: string; c: ConversationView; onClose: () => void; onDone: (d: ConversationDetail) => void }) {
  const [people, setPeople] = useState<{ id: string; name: string; here: boolean; signedIn: boolean; supervisor: boolean }[]>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  useEffect(() => {
    api
      .handTargets(domain, c.id)
      .then((r) => setPeople(r.people))
      .catch((e) => setError(errorText(e)));
  }, [domain, c.id]);
  const t = c.team!;
  return (
    <Dialog open onClose={onClose} title="Hand to someone" subtitle={`${c.title} · ${teamLabel(t)}`}>
      {error && <Notice tone="crit">{error}</Notice>}
      {!people ? (
        <p className="muted">Loading {t.name}’s people…</p>
      ) : !people.some((p) => p.id !== t.owner?.id) ? (
        // Only whoever has it (or nobody) uses Stitch in the team: say so, not an empty list.
        <p className="muted">Nobody else in {t.name} uses Stitch yet.</p>
      ) : (
        <ul className="hand-list">
          {people
            .filter((p) => p.id !== t.owner?.id)
            .map((p) => (
              <li key={p.id}>
                <span className="grow">
                  <strong>{p.name}</strong>
                  <span className="muted">
                    {handToWords(p, t.name)}
                  </span>
                </span>
                <Button
                  size="small"
                  disabled={busy !== ''}
                  onClick={async () => {
                    setBusy(p.id);
                    setError('');
                    try {
                      onDone(await api.handText(domain, c.id, p.id));
                    } catch (e) {
                      setError(errorText(e));
                    } finally {
                      setBusy('');
                    }
                  }}
                >
                  Hand to {p.name.split(/\s+/)[0]}
                </Button>
              </li>
            ))}
        </ul>
      )}
    </Dialog>
  );
}

// --- the summary beside it (Stitch AI) -------------------------------------------------------------------

const LANE_KEY = 'stitch.teamTexts.summary';

/** The summary lane open or closed (remembered on this device; open to start with). */
export function useSummaryOpen(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(() => {
    try {
      return localStorage.getItem(LANE_KEY) !== 'off';
    } catch {
      return true;
    }
  });
  return [
    on,
    (next) => {
      setOn(next);
      try {
        localStorage.setItem(LANE_KEY, next ? 'on' : 'off');
      } catch {
        // Not kept.
      }
    }
  ];
}

/** Puts words in this conversation's message box (the summary's suggested reply). */
export function useSuggestion(conversationId: string, put: (text: string) => void): void {
  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<{ conversationId: string; text: string }>).detail;
      if (d?.conversationId === conversationId) put(d.text);
    };
    window.addEventListener('stitch:suggest', on);
    return () => window.removeEventListener('stitch:suggest', on);
  }, [conversationId, put]);
}

/**
 * The third lane beside a shared conversation: what's going on, what's still open, earlier
 * conversations with the number, who has had it, and a reply to use or ignore. Team only.
 */
export function SummaryLane({ domain, c, onClose, onOff }: { domain: string; c: ConversationView; onClose: () => void; /** Off (not set up, or switched off): steps aside on its own. */ onOff?: () => void }) {
  const [r, setR] = useState<TextSummaryResult>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const load = async (refresh = false) => {
    setBusy(true);
    setError('');
    try {
      const got = await api.textSummary(domain, c.id, refresh);
      setR(got);
      if (got.off) onOff?.();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, c.id]);
  // Newer texts: made again (the server does it at most once a minute).
  const [nudge, setNudge] = useState(0);
  useMessagesLive((e) => {
    if (e.tenant === domain && e.conversationId === c.id && e.type === 'message') setNudge((n) => n + 1);
  });
  useEffect(() => {
    if (!nudge) return;
    const t = window.setTimeout(() => void load(), 4000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nudge]);
  const s = r?.summary;
  return (
    // Private like the texts it sums up: never in Live help or a session recording (3144).
    <aside className="summary-lane" aria-label="Summary" data-private="">
      <div className="summary-head">
        <Icon name="spark" size={16} />
        <h2>Summary</h2>
        <span className="summary-ai">Stitch AI</span>
        <button type="button" className="icon-btn" aria-label="Close the summary" title="Close the summary" onClick={onClose}>
          <Icon name="x" size={16} strokeWidth={2.2} />
        </button>
      </div>
      <div className="summary-body">
        {error && <Notice tone="crit">{error}</Notice>}
        {!r && !error && <p className="muted">Reading the conversation…</p>}
        {r?.off && <p className="muted">{r.off}</p>}
        {r && !r.off && !s && <p className="muted">{busy ? 'Writing a summary…' : 'Nothing to sum up yet.'}</p>}
        {s && (
          <>
            <section>
              <h3>What’s going on</h3>
              <p>{s.now}</p>
            </section>
            {s.open.length > 0 && (
              <section>
                <h3>Still open</h3>
                <ul>
                  {s.open.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              </section>
            )}
            {s.before && (
              <section>
                <h3>Before</h3>
                <p>{s.before}</p>
              </section>
            )}
            {s.people.length > 0 && (
              <section>
                <h3>Who’s had it</h3>
                <p>{s.people.join(' → ')}</p>
              </section>
            )}
            {s.suggestion && (
              <section className="summary-suggest">
                <h3>A reply you could send</h3>
                <p>“{s.suggestion}”</p>
                <Button size="small" onClick={() => window.dispatchEvent(new CustomEvent('stitch:suggest', { detail: { conversationId: c.id, text: s.suggestion } }))}>
                  Use this
                </Button>
              </section>
            )}
            <p className="summary-foot muted">
              {r?.stale ? 'Newer texts came in. ' : ''}Only your team sees this; it’s never sent to the customer. Check it before you rely on it.
            </p>
          </>
        )}
        {r && !r.off && (
          <Button size="small" variant="ghost" icon="refresh" disabled={busy} onClick={() => void load(true)}>
            {busy ? 'Updating…' : 'Update'}
          </Button>
        )}
      </div>
    </aside>
  );
}

// --- Call (2973) ------------------------------------------------------------------------------------------

/**
 * Call the number you're texting with, in the Stitch app (Joey, Oct 30: "Right now I have to copy and
 * paste their number into the my phone dialer"). Shown when calls in the app are set up for you.
 */
export function CallButton({ number, label }: { number: string; label: string }) {
  const calls = useCalls();
  const [busy, setBusy] = useState(false);
  if (!calls.on || !number) return null;
  const why = !calls.available ? (calls.reason ?? 'Calls in the app aren’t working right now.') : calls.busy ? 'One moment…' : '';
  return (
    <button
      type="button"
      className="msg-tool big"
      aria-label={`Call ${label}`}
      title={why || `Call ${label}`}
      disabled={Boolean(why) || busy}
      onClick={async () => {
        setBusy(true);
        try {
          await dial(number);
        } finally {
          setBusy(false);
        }
      }}
    >
      <Icon name="phone" size={17} />
    </button>
  );
}
